/** @jsx jsx */
import { jsx } from '@emotion/react';

import * as React from 'react';

import type { ApolloQueryResult } from 'apollo-client';
import { compose } from 'recompose';

import { tupleToStringKey } from 'js/lib/stringKeyTuple';

import { Grid, Typography2 } from '@coursera/cds-core';
import { Card, StyleSheet, color, css, spacing } from '@coursera/coursera-ui';

import type {
  EnterpriseBadgeCollectionsQuery as EnterpriseBadgeCollectionsQueryData,
  EnterpriseBadgeCollectionsQueryVariables,
} from 'bundles/enterprise-learner-onboarding/queries/__generated__/EnterpriseBadgeCollectionsQuery';
import type { CircleMenuItem } from 'bundles/enterprise-legacy-learner-home/components/multiCourseProductCard/MultiCourseProductCirclesMenu';
import MultiCourseProductEnrolledCard from 'bundles/enterprise-legacy-learner-home/components/multiCourseProductCard/MultiCourseProductEnrolledCard';
import { removeCredentialFromBadgeTitle } from 'bundles/enterprise-legacy-learner-home/utils/EnterpriseBadgeUtils';
import Naptime from 'bundles/naptimejs';
// @ts-expect-error TS7016 Untyped import http://go.dkandu.me/strict-ts-migration#TS7016
import ProgramCurriculumProductsV1 from 'bundles/naptimejs/resources/programCurriculumProducts.v1';
import type { EnterpriseBadge } from 'bundles/page-config-common/providers/enterprise/EnterpriseBadgeCollectionsProvider';

import _t from 'i18n!nls/course-cards';

const MIN_CARD_HEIGHT = 112;

const styles = StyleSheet.create({
  BadgeCard: {
    position: 'relative',
    backgroundColor: color.white,
    marginBottom: spacing.md,
    overflow: 'hidden',
  },
});

type PropsFromCaller = {
  programId: string;
  userId: number;
  enterpriseBadge: EnterpriseBadge;
  refetchEnterpriseBadgeCollections?: (
    variables?: EnterpriseBadgeCollectionsQueryVariables
  ) => Promise<ApolloQueryResult<EnterpriseBadgeCollectionsQueryData>>;
  isEnterpriseProfileBar?: boolean;
};

type PropsFromNaptime = {
  curriculumProducts?: Array<ProgramCurriculumProductsV1>;
};

type Props = PropsFromCaller & PropsFromNaptime;

export function BadgeCard({
  programId,
  userId,
  enterpriseBadge,
  curriculumProducts,
  refetchEnterpriseBadgeCollections,
  isEnterpriseProfileBar,
}: Props) {
  if (!enterpriseBadge || !curriculumProducts) {
    return null;
  }
  const cardStyles = {
    minHeight: MIN_CARD_HEIGHT,
    marginBottom: spacing.md,
    padding: 0,
    marginTop: '2px',
    marginLeft: '2px',
    marginRight: '2px',
    boxShadow: '0px 1px 4px var(--cds-color-darken-200)',
    border: '1px solid var(--cds-color-grey-25)',
  };

  const cardStylesEnterpriseProfileBar = {
    minHeight: MIN_CARD_HEIGHT,
    marginBottom: spacing.md,
    boxShadow: 'none',
    gap: 'var(--cds-spacing-200)',
    borderRadius: 'var(--cds-border-radius-200)',
    border: '1px solid var(--cds-color-grey-100)',
    paddingTop: 'var(--cds-spacing-200)',
  };

  const collectionId = enterpriseBadge.collectionId;
  const badgeTitle = removeCredentialFromBadgeTitle(enterpriseBadge.title);
  const badgeProductIds = enterpriseBadge.productIds.map((productId) => productId.id);
  const activeEnrolledCourseIds: Array<string> = curriculumProducts
    .filter((session) => session.isEnrolled)
    .map((session) => session.courseId);

  const completedCourseIds = curriculumProducts
    .filter((product) => product.isCompleted)
    .map((product) => product.courseId);
  const selectedCourseId = badgeProductIds[0];
  const courseCount = badgeProductIds.length;

  const circleMenuData: CircleMenuItem[] = badgeProductIds.map((id, index) => ({
    courseId: id,
    isActive: selectedCourseId === id,
    isEnrolled: activeEnrolledCourseIds.includes(id),
    isCompleted: completedCourseIds.includes(id),
    index,
    isCapstone: false,
  }));

  return (
    <div>
      {enterpriseBadge.userState === 'ISSUED' && (
        <Grid
          container
          alignItems="center"
          css={{
            backgroundColor: 'var(--cds-color-blue-50)',
            padding: 'var(--cds-spacing-200)',
            borderRadius: '2px',
          }}
        >
          <Grid xs={12}>
            <Typography2 component="p" variant="bodySecondary">
              {_t("You've completed all courses in the #{title}.", {
                title: removeCredentialFromBadgeTitle(enterpriseBadge.title),
              })}
            </Typography2>
          </Grid>
        </Grid>
      )}
      <Card
        tagAttributes={{
          xsMinWidth: true,
        }}
        isInteractive={true}
        style={isEnterpriseProfileBar ? cardStylesEnterpriseProfileBar : cardStyles}
      >
        <div {...css('m-b-1', styles.BadgeCard)} data-e2e={`BadgeCard~${enterpriseBadge.badgeTemplateId}`}>
          {userId && (
            <MultiCourseProductEnrolledCard
              id={enterpriseBadge.badgeTemplateId}
              name={badgeTitle}
              logo={enterpriseBadge.imageUrl}
              description={enterpriseBadge.description || ''}
              programId={programId}
              collectionId={collectionId}
              userId={userId}
              itemType="BADGE"
              canUnenroll={true}
              courseCount={courseCount || 0}
              // @ts-expect-error TS2322 Unchecked index access, value is possibly undefined see https://www.typescriptlang.org/tsconfig/#noUncheckedIndexedAccess
              firstCourseId={selectedCourseId}
              circleMenuData={circleMenuData}
              refetchEnterpriseBadgeCollections={refetchEnterpriseBadgeCollections}
            />
          )}
        </div>
      </Card>
    </div>
  );
}

const BadgeCardWithData = compose<Props, PropsFromCaller>(
  Naptime.createContainer(({ enterpriseBadge, programId }: PropsFromCaller) => {
    const courseIds: Array<string> = enterpriseBadge.productIds.map((productId) => productId.id);
    const tupleCourseIds: Array<string> = courseIds.map((id) =>
      tupleToStringKey([programId, 'VerifiedCertificate', id])
    );
    return {
      curriculumProducts: ProgramCurriculumProductsV1.multiGet(tupleCourseIds, {}),
    };
  })
)(BadgeCard);

export default BadgeCardWithData;
